/**
 * <s-nav-dropdown> — Navigation dropdown that opens on hover or keyboard focus
 * and closes when neither condition is active. No commands — purely event-driven.
 *
 * Required child markup:
 *   a[data-nav-trigger] — The trigger anchor; `aria-expanded` is managed by this component
 *                        (set to "false" on connect, "true"/"false" on show/hide). No
 *                        aria-haspopup: this is APG disclosure-navigation, not an ARIA menu.
 *   .nav-dropdown      — The dropdown panel container; must be present or the component
 *                        exits connectedCallback silently (no-op).
 *
 * Nested submenus:
 *   Each .nav-dropdown-group whose direct-child trigger is `a[data-nav-trigger]` also gets
 *   its `aria-expanded` mirrored from the CSS-driven (:hover / :focus-within) submenu
 *   reveal, so multi-level menus expose disclosure state to assistive tech.
 *
 * Show/hide logic:
 *   Opens on mouseenter or focusin within the element.
 *   Closes on mouseleave when focus is not inside; closes on focusout when hover is not active.
 */
class SNavDropdown extends HTMLElement {
  #trigger;
  #ac;
  #hovered = false;
  #focused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#trigger = this.querySelector(':scope > a[data-nav-trigger]');
    const dropdown = this.querySelector('.nav-dropdown');
    if (!this.#trigger || !dropdown) return;

    this.#trigger.setAttribute('aria-expanded', 'false');

    this.addEventListener('mouseenter', this.#onMouseEnter, opts);
    this.addEventListener('mouseleave', this.#onMouseLeave, opts);
    this.addEventListener('focusin', this.#onFocusIn, opts);
    this.addEventListener('focusout', this.#handleFocusOut, opts);

    // Nested submenu groups: mirror the CSS reveal (:hover / :focus-within) into
    // aria-expanded on each group's trigger link. The visual reveal stays CSS-driven.
    for (const group of this.querySelectorAll('.nav-dropdown-group')) {
      const trigger = group.querySelector(':scope > a[data-nav-trigger]');
      if (!trigger) continue;
      trigger.setAttribute('aria-expanded', 'false');
      const state = { hovered: false, focused: false };
      const sync = () => trigger.setAttribute('aria-expanded', state.hovered || state.focused ? 'true' : 'false');
      group.addEventListener('mouseenter', () => { state.hovered = true; sync(); }, opts);
      group.addEventListener('mouseleave', () => { state.hovered = false; sync(); }, opts);
      group.addEventListener('focusin', () => { state.focused = true; sync(); }, opts);
      group.addEventListener('focusout', (e) => {
        if (!group.contains(e.relatedTarget)) { state.focused = false; sync(); }
      }, opts);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onMouseEnter = () => {
    this.#hovered = true;
    this.#show();
  };

  #onMouseLeave = () => {
    this.#hovered = false;
    if (!this.#focused) this.#hide();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#show();
  };

  #handleFocusOut = (e) => {
    if (!this.contains(e.relatedTarget)) {
      this.#focused = false;
      if (!this.#hovered) this.#hide();
    }
  };

  #show() {
    this.#trigger?.setAttribute('aria-expanded', 'true');
  }

  #hide() {
    this.#trigger?.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('s-nav-dropdown', SNavDropdown);
